// Разбиение потока на страницы по правилам Word (как собирает writer/builder.py):
//  - строки абзаца перетекают на следующую страницу по одной; запрет висячих строк: не меньше 2 строк внизу и вверху
//    (абзац из 2-3 строк не делится);
//  - «не отрывать от следующего» - ровно как в DOCX writer (флаги keepNext/keepLines): заголовки и структурные
//    элементы, рисунок (картинка - с подписью: рисунок с подписью здесь один неделимый блок), название таблицы (с
//    первой строкой таблицы) и листинга (с первой строкой кода); «не разрывать» (keepLines) - только заголовки.
//    Обычные абзацы, «…:» перед перечислением/формулой, позиции перечислений, формулы, экспликация «где», головка
//    таблицы - БЕЗ «не отрывать»: Word может оставить их внизу страницы (как в собранном DOCX);
//  - новая страница: раздел (заголовок 1 уровня), структурный элемент, приложение, раздел об ИИ, после разрыва страницы;
//  - таблица делится по строкам (строка не делится, объединённые по вертикали строки - вместе); на следующей странице -
//    «Продолжение таблицы N» и повтор головки. Если на странице остались только название и головка (у головки нет
//    «не отрывать»), Word оставляет их там, а writer/tablesplit.py такую таблицу не делит: дальше - только повтор
//    головки, без «Продолжение таблицы» (и у следующих частей той же таблицы);
//  - листинг делится по строкам кода (не меньше 2 строк внизу и вверху), как таблица: в DOCX код делится на части
//    (абзацы кода в своей рамке), над следующей частью - «Продолжение листинга N» / над последней - «Окончание
//    листинга N» (стиль подписи листинга, «не отрывать» с первой строкой кода). Часть внизу страницы - без нижней линии
//    рамки (как таблица, writer/listingsplit.py): строк на странице столько же, сколько у неразделённого кода; на
//    странице продолжения - надпись и верхняя линия рамки (overhead);
//  - интервал перед абзацем в начале страницы не учитывается (как в Word при естественном разрыве);
//  - сноски - внизу страницы, где стоит ссылка (место под ними вычитается из высоты страницы).
import { BODY_H, BODY_TOP, PAGE_H, M_BOTTOM, STRIDE } from './geometry';
import type { Atom, Page, TableInfo } from './types';

export interface EngineOptions {
  /** высота сноски по id (px без масштаба) */
  fnHeight: (id: string) => number;
  /** высота разделителя сносок (если на странице есть сноски) */
  fnSep: number;
  /** высота подписи «Продолжение таблицы N» / «Продолжение листинга N» с интервалом после (стили названия таблицы и
   *  подписи листинга одинаковы: TNR 14 одинарный, 6 пт после) */
  contLabelH: number;
  /** верх текста на листе (px без масштаба; по умолчанию - основная часть, BODY_TOP; листы перед текстом - M_TOP) */
  top?: number;
  /** первая страница начинается там, где стоит первый атом (лист перед текстом: над текстом заголовок листа) */
  firstInPlace?: boolean;
}

const EPS = 0.75;

export function paginate(atoms: Atom[], opt: EngineOptions): Page[] {
  const TOP = opt.top ?? BODY_TOP;
  const contentTop = (i: number) => i * STRIDE + TOP;
  const contentBottom = (i: number) => i * STRIDE + PAGE_H - M_BOTTOM;
  const pages: Page[] = [];
  let pi = 0;
  const newPage = (start: number, prevTable?: TableInfo): Page => {
    const a = atoms[start];
    let overhead = 0;
    let cont: Page['cont'];
    let lcont: Page['lcont'];
    if (a && a.kind === 'row' && a.table && prevTable === a.table && !a.head) {
      // на прошлой странице от таблицы - только название и головка: tablesplit не делит таблицу (k <= hdr) - Word
      // повторяет головку, подписи продолжения нет ни здесь, ни дальше
      if (!dataBefore(start, a.table)) unsplit.add(a.table);
      const label = !unsplit.has(a.table);
      overhead = (label ? opt.contLabelH : 0) + a.table.headH;
      cont = { table: a.table, final: false, label };
    } else if (a && a.kind === 'line' && a.listing && a.li > 0 && a.brk === 'code') {
      // продолжение листинга: надпись «Продолжение/Окончание листинга N» и верхняя линия рамки новой части
      overhead = opt.contLabelH + a.listing.padTop;
      lcont = { listing: a.listing, final: false };
    }
    const top = pi === 0 && opt.firstInPlace && a ? a.top : contentTop(pi) + overhead;
    return { start, shift: a ? top - a.top : 0, overhead, cont, lcont, fns: [], fnH: 0, lastBottom: top, end: start - 1 };
  };
  /** таблицы, которые writer не делит (первый разрыв - сразу после головки) */
  const unsplit = new Set<TableInfo>();
  /** есть ли строки данных таблицы t перед атомом start (на прошлой странице) */
  function dataBefore(start: number, t: TableInfo): boolean {
    for (let j = start - 1; j >= 0 && atoms[j].table === t; j--) if (atoms[j].kind === 'row' && !atoms[j].head) return true;
    return false;
  }
  let p = newPage(0);
  pages.push(p);
  const fnTotal = (fns: string[]) => (fns.length ? opt.fnSep + fns.reduce((s, id) => s + opt.fnHeight(id), 0) : 0);
  const recomputeFns = (pg: Page, from: number, to: number) => {
    const ids: string[] = [];
    for (let j = from; j < to; j++) for (const id of atoms[j].fns || []) if (!ids.includes(id)) ids.push(id);
    pg.fns = ids;
    pg.fnH = fnTotal(ids);
  };
  /** абзац целиком -> строки; число строк (0 - не раскрылся) */
  const expandAt = (i: number): number => {
    const a = atoms[i];
    if (!a.expand) return 0;
    const lines = a.expand();
    a.expand = undefined;
    if (!lines.length) return 0;
    atoms.splice(i, 1, ...lines);
    return lines.length;
  };

  let i = 0;
  let guard = 0;
  while (i < atoms.length && guard++ < atoms.length * 8 + 1000) {
    const a = atoms[i];
    if (a.force && i > p.start && a.brk) {
      p.end = i - 1;
      pi++;
      p = newPage(i);
      pages.push(p);
      continue;
    }
    const fns = a.fns ? [...p.fns, ...a.fns.filter((x) => !p.fns.includes(x))] : p.fns;
    const fnH = a.fns ? fnTotal(fns) : p.fnH;
    const bottom = a.bottom + p.shift;
    const limit = contentBottom(pi) - fnH;
    // строка таблицы должна поместиться ЦЕЛИКОМ вместе с нижней линией (Word переносит строку, если её низ хоть
    // немного ниже поля; прямоугольник строки в браузере не включает нижнюю половину линии - 0,5 px); допуск на
    // погрешность замера - только у текста
    if (bottom - (a.slack || 0) <= limit + (a.kind === 'row' ? -0.5 : EPS) || a.kind === 'zero') {
      p.fns = fns;
      p.fnH = fnH;
      p.lastBottom = Math.max(p.lastBottom, bottom);
      p.end = i;
      i++;
      continue;
    }
    // не помещается
    if (a.kind === 'para' && a.expand) { if (expandAt(i)) continue; }
    if (i === p.start) {
      // атом выше целой страницы (огромный рисунок) - остаётся, дальше новая страница
      p.fns = fns; p.fnH = fnH; p.lastBottom = bottom; p.end = i;
      i++;
      if (i < atoms.length) { pi++; p = newPage(i, a.table); pages.push(p); }
      continue;
    }
    let b = chooseBreak(i);
    if (b <= p.start || !atoms[b]?.brk) b = fallback(i);
    if (b > p.start && atoms[b].brk === 'inline' && atoms[b].pos < 0) b = b - atoms[b].li > p.start ? b - atoms[b].li : fallback(i);
    if (b <= p.start) {
      // сдвинуть некуда: атом остаётся на странице (переполнение), дальше - новая страница
      p.lastBottom = Math.max(p.lastBottom, bottom); p.end = i;
      i++;
      if (i < atoms.length) { pi++; p = newPage(i, a.table); pages.push(p); }
      continue;
    }
    recomputeFns(p, p.start, b);
    p.end = b - 1;
    // часть листинга до разрыва - без нижней линии рамки (writer/listingsplit.py, как у таблицы)
    p.lastBottom = atoms[b - 1].bottom + p.shift;
    const prevTable = atoms[b - 1].table;
    const open = atoms[b - 1].kind === 'row' && atoms[b].kind === 'row' && atoms[b - 1].table === atoms[b].table;
    const old = p;
    pi++;
    p = newPage(b, prevTable);
    // нижняя линия не проводится только у части таблицы, которую writer отделил (tablesplit.py)
    if (open && (!p.cont || p.cont.label)) old.openRow = b - 1;
    pages.push(p);
    i = b;
  }
  // «Окончание таблицы N» - у последней части
  for (let q = pages.length - 1; q >= 0; q--) {
    const c = pages[q].cont;
    if (!c) continue;
    if (unsplit.has(c.table)) c.label = false;
    const later = pages.slice(q + 1).some((x) => x.cont && x.cont.table === c.table);
    c.final = !later;
  }
  // «Окончание листинга N» - у последней части
  for (let q = pages.length - 1; q >= 0; q--) {
    const c = pages[q].lcont;
    if (c) c.final = !pages.slice(q + 1).some((x) => x.lcont && x.lcont.listing === c.listing);
  }
  return pages;

  /** Первая строка следующей страницы, если атом i не помещается. */
  function chooseBreak(i0: number): number {
    let b = i0;
    const a = atoms[b];
    if (a.kind === 'line' && a.li > 0) {
      const firstOfGrp = b - a.li;
      const first = Math.max(firstOfGrp, p.start);
      if (b - first < 2) b = first;                                            // одна строка внизу страницы
      else if (a.ln - a.li < 2) { b = b - 1; if (b - first < 2) b = first; }    // одна строка вверху следующей
      // листинг: часть внизу страницы - без нижней линии рамки (writer/listingsplit.py), места под линию не нужно -
      // строк на странице столько же, сколько у неразделённого абзаца кода в Word
    } else if (a.kind === 'row' && a.table) {
      const t = a.table;
      // строка не делится; объединённые по вертикали и строки головки - вместе. Головка без «не отрывать»: она может
      // остаться внизу страницы одна (с названием - у названия «не отрывать»), как у Word в собранном DOCX
      while (b > p.start && atoms[b].kind === 'row' && atoms[b].table === t && !atoms[b].rowBreakOk) b--;
    }
    // «не отрывать от следующего»
    let steps = 0;
    while (b > p.start && atoms[b - 1].keepNext && steps++ < 500) {
      if (atoms[b - 1].kind === 'para') {
        const n = atoms[b - 1].expand ? expandAt(b - 1) : 0;
        if (!n) { b = b - 1; continue; }
        b = b - 1 + n;
      }
      const q = atoms[b - 1];
      if (q.kind === 'line') {
        const firstOfGrp = b - 1 - q.li;
        const first = Math.max(firstOfGrp, p.start);
        if (q.ln <= 3 || q.keepLines) b = first;
        else { b = b - 2; if (b - first < 2) b = first; }
      } else if (q.kind === 'row' && q.table) {
        const tf = q.table.firstAtom ? atoms.lastIndexOf(q.table.firstAtom, b) : -1;
        b = tf >= 0 ? tf : b - 1;
      } else b = b - 1;
    }
    return b;
  }

  /** «Не отрывать» не выполнимо (вся страница - цепочка): разрыв как без него. */
  function fallback(i0: number): number {
    const a = atoms[i0];
    if (a.kind === 'line' && a.li >= 2 && a.ln - a.li >= 2) return i0;
    let b = i0;
    while (b > p.start && !atoms[b].brk) b--;
    return b;
  }
}

/** Страница атома (для оглавления, навигатора, строки состояния). */
export function pageOfAtom(pages: Page[], idx: number): number {
  let lo = 0;
  let hi = pages.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (pages[mid].start <= idx) lo = mid; else hi = mid - 1;
  }
  return lo;
}

export const TEXT_HEIGHT = BODY_H;
